import { supabase } from '../../config/supabase';
import { DEFAULT_NOTE_CATEGORY } from '../common/noteCategories';

import heic2any from "heic2any";
import imageCompression from "browser-image-compression";
import { sendEstimationCompletedNotifications } from './notifications';

const normalizeUploadFile = async (inputFile) => {
    if (!inputFile) return inputFile;

    const ext = inputFile.name?.split('.').pop()?.toLowerCase();

    let workingFile = inputFile;

    // Convert HEIC / HEIF to JPEG
    if (ext === "heic" || ext === "heif") {
        const jpgBlob = await heic2any({
            blob: inputFile,
            toType: "image/jpeg",
            quality: 0.65,
        });

        workingFile = new File(
            [jpgBlob],
            inputFile.name.replace(/\.(heic|heif)$/i, ".jpg"),
            {
                type: "image/jpeg",
                lastModified: inputFile.lastModified,
            }
        );
    }

    if (workingFile.size < 400000) return workingFile;

    // WhatsApp-style aggressive compression
    if (workingFile.type.startsWith("image/")) {
        workingFile = await imageCompression(workingFile, {
            maxSizeMB: 0.8,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            initialQuality: 0.8,
            alwaysKeepResolution: false,
        });
    }

    return workingFile;
};

const sanitizeFileName = (name) => {
    // split name & extension
    const extMatch = name.match(/\.[^/.]+$/);
    const ext = extMatch ? extMatch[0] : '';
    const base = name.replace(ext, '');

    // normalize
    const normalized = base
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, '');

    // replace invalid chars
    const cleaned = normalized
        .replace(/[^a-zA-Z0-9-_\. ]/g, '') // remove special chars like [ ] ( ) #
        .replace(/\s+/g, '-')              // spaces → dash
        .replace(/-+/g, '-')               // collapse multiple dashes
        .toLowerCase();

    return `${cleaned}${ext.toLowerCase()}`;
}



export const uploadDeliveryPhotosBulk = async (
    jobId,
    files,
    quoteType,
    userId,
    userName,
    userRole,
    companySlug,
    deliveryCount = 0,
    childId = null,
    childName = null
) => {
    try {
        // Normalize/compress all files in parallel
        const processedFiles = await Promise.all(
            files.map(async (file) => {
                const normalizedFile = await normalizeUploadFile(file);

                const safeName = sanitizeFileName(normalizedFile.name);

                const filename = `${companySlug}/${jobId}/${Date.now()}-${Math.random()
                    .toString(36)
                    .substring(2, 8)}-${safeName}`;

                return {
                    originalFile: file,
                    normalizedFile,
                    filename,
                };
            })
        );

        // Upload all files in parallel
        await Promise.all(
            processedFiles.map(async ({ normalizedFile, filename }) => {
                const { error } = await supabase.storage
                    .from("delivery-uploads")
                    .upload(filename, normalizedFile, {
                        cacheControl: "3600",
                        upsert: false,
                    });

                if (error) throw error;
            })
        );

        // Build DB rows
        const fileRows = processedFiles.map(({ originalFile, filename }) => {
            const {
                data: { publicUrl },
            } = supabase.storage
                .from("delivery-uploads")
                .getPublicUrl(filename);

            return {
                job_id: jobId,
                filename,
                original_name: originalFile.name,
                url: publicUrl,
                uploaded_by_user_id: userId,
                uploaded_by_name: userName,
                uploaded_by_role: userRole,
                file_type: "delivery",
                quote_type: quoteType,
                delivery_count: deliveryCount,
                child_id: childId || null,
                child_unique_id: childName || null,
            };
        });

        // Single bulk insert
        const { data, error } = await supabase
            .from("job_files")
            .insert(fileRows)
            .select();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error("Bulk delivery upload failed:", error);
        throw error;
    }
};








/**
 * Fetch single job/assignment with all related data
 * @param {string} jobId - The job ID (can be UUID or unique_id)
 * @param {string} companyId - The company ID
 * @returns {Promise<Object>} Job assignment with all relations
 */
export const getSingleAssignment = async (jobId, companyId) => {
    try {
        // Try to fetch by UUID first, then by unique_id if that fails
        // Filter job_quotes to only get initial quotes (delivery_count is null or 0)
        let query = supabase
            .from('jobs')
            .select(`
                *,
                job_quotes(*),
                job_designers(*),
                job_estimators(*),
                job_production(*),
                job_deliveries(*),
                job_notes(*),
                job_files(*),
                job_floors(*),
                job_floor_specs(*),
                job_action_logs(*)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false);

        // Check if jobId is a UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        if (uuidRegex.test(jobId)) {
            query = query.eq('id', jobId);
        } else {
            query = query.eq('unique_id', jobId);
        }

        const { data, error } = await query.single();

        if (error) throw error;

        let customerType = null;
        let customerNote = null;
        let customerNotes = [];

        if (data?.customer_name) {
            const { data: customerRows, error: customerError } = await supabase
                .from('customers')
                .select('id, customer_type, note, email, phone, created_at')
                .eq('company_id', companyId)
                .eq('name', data.customer_name)
                .order('created_at', { ascending: false });

            if (customerError) {
                console.warn('Could not fetch customer data:', customerError);
            }

            const rows = customerRows || [];

            const currentCustomer =
                rows.find(c => data.customer_mail && c.email === data.customer_mail) ||
                rows.find(c => data.customer_number && c.phone === data.customer_number) ||
                rows[0] ||
                null;

            customerType = currentCustomer?.customer_type || null;
            customerNote = currentCustomer?.note || null;

            customerNotes = rows
                .filter(c => c.note?.trim())
                .sort((a, b) => {
                    if (a.id === currentCustomer?.id) return -1;
                    if (b.id === currentCustomer?.id) return 1;
                    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                });
        }

        // Filter job_quotes to only include initial quotes (delivery_count is null, undefined, or 0)
        // if (data && data.job_quotes) {
        //     data.job_quotes = data.job_quotes.filter(quote =>
        //         quote.delivery_count === null || quote.delivery_count === undefined || quote.delivery_count === 0
        //     );
        // }

        return {
            ...data,
            customer_type: customerType,
            customer_note: customerNote,
            customer_notes: customerNotes,
        };
    } catch (error) {
        console.error('Error fetching assignment:', error);
        throw error;
    }
};

/**
 * Add a note to a job
 * @param {string} jobId - The job ID
 * @param {string} content - Note content
 * @param {string} userId - User ID adding the note
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @returns {Promise<Object>} Created note
 */
export const addJobNote = async (jobId, content, userId, userName, userRole, department = null, category = DEFAULT_NOTE_CATEGORY) => {
    try {
        const { data, error } = await supabase
            .from('job_notes')
            .insert({
                job_id: jobId,
                content: content,
                added_by_user_id: userId,
                added_by_name: userName,
                added_by_role: userRole,
                child_unique_id: department?.child_unique_id || null,
                child_id: department?.id || null,
                category: category || DEFAULT_NOTE_CATEGORY
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error adding note:', error);
        throw error;
    }
};

/**
 * Update a job note
 * @param {string} noteId - The note ID
 * @param {string} content - Updated content
 * @param {string} userId - User ID editing the note
 * @returns {Promise<Object>} Updated note
 */
export const updateJobNote = async (noteId, content, userId, category = DEFAULT_NOTE_CATEGORY) => {
    try {
        const { data, error } = await supabase
            .from('job_notes')
            .update({
                content: content,
                category: category || DEFAULT_NOTE_CATEGORY,
                is_edited: true,
                edited_at: new Date().toISOString()
            })
            .eq('id', noteId)
            .eq('added_by_user_id', userId) // Ensure user can only edit their own notes
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error updating note:', error);
        throw error;
    }
};

/**
 * Delete a job note
 * @param {string} noteId - The note ID
 * @param {string} userId - User ID deleting the note
 * @param {string} userRole - User role
 * @returns {Promise<void>}
 */
export const deleteJobNote = async (noteId, userId, userRole) => {
    try {
        let query = supabase
            .from('job_notes')
            .delete()
            .eq('id', noteId);

        // If NOT admin or super_admin, restrict to own notes only
        if (!['admin', 'super_admin'].includes(userRole)) {
            query = query.eq('added_by_user_id', userId);
        }

        const { error, count } = await query.select();

        if (error) throw error;

        // Optional: detect unauthorized delete attempts
        if (count === 0) {
            throw new Error('You are not authorized to delete this note.');
        }

    } catch (error) {
        console.error('Error deleting note:', error);
        throw error;
    }
};

export const cancelJob = async (jobId, companyId, userProfile) => {
    const { data, error } = await supabase
        .from('jobs')
        .update({
            is_cancelled: true,
            updated_at: new Date().toISOString()
        })
        .eq('id', jobId)
        .eq('company_id', companyId)
        .select()
        .single();

    if (error) throw error;

    await supabase.from('job_action_logs').insert({
        job_id: jobId,
        company_id: companyId,
        user_id: userProfile?.id,
        user_name: userProfile?.username || userProfile?.full_name,
        user_role: userProfile?.role,
        action: 'job_cancelled',
        details: 'Job was cancelled',
        timestamp: new Date().toISOString()
    });

    return data;
};

export const restoreCancelledJob = async (jobId, companyId, userProfile) => {
    const { data, error } = await supabase
        .from('jobs')
        .update({
            is_cancelled: false,
            updated_at: new Date().toISOString()
        })
        .eq('id', jobId)
        .eq('company_id', companyId)
        .select()
        .single();

    if (error) throw error;

    await supabase.from('job_action_logs').insert({
        job_id: jobId,
        company_id: companyId,
        user_id: userProfile?.id,
        user_name: userProfile?.username || userProfile?.full_name,
        user_role: userProfile?.role,
        action: 'job_restored',
        details: 'Cancelled job was restored',
        timestamp: new Date().toISOString()
    });

    return data;
};

/**
 * Delete a job note
 * @param {string} noteId - The note ID
 * @param {string} userId - User ID deleting the note
 * @param {string} userRole - User role
 * @returns {Promise<void>}
 */
export const setImportantJobNote = async (noteId, isImportant) => {
    try {
        const { error } = await supabase
            .from('job_notes')
            .update({ is_important: isImportant })
            .eq('id', noteId);

        if (error) throw error;
    } catch (error) {
        console.error('Error updating note importance:', error);
        throw error;
    }
};

/**
 * Get available designers for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of designers
 */
export const getAvailableDesigners = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email, role, department')
            .eq('company_id', companyId)
            .or('role.eq.designer,permissions.cs.{"ASSIGN_DESIGNERS"}')
            .eq('status', 'active')
            .order('full_name');

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching designers:', error);
        throw error;
    }
};

/**
 * Get available estimators for a company
 * @param {string} companyId - The company ID
 * @param {boolean} allowAnyUserAsEstimator - If true, return all active users
 * @returns {Promise<Array>} Array of estimators/users
 */
export const getAvailableEstimators = async (
    companyId,
    allowAnyUserAsEstimator = false
) => {
    try {
        let query = supabase
            .from("users")
            .select("id, username, full_name, email, role")
            .eq("company_id", companyId)
            .eq("status", "active");

        if (!allowAnyUserAsEstimator) {
            query = query.or('role.eq.estimator,permissions.cs.{"ASSIGN_ESTIMATORS"}');
        }

        const { data, error } = await query
            .order("role", { ascending: true })
            .order("full_name", { ascending: true });

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error("Error fetching estimators:", error);
        throw error;
    }
};

/**
 * Get available drivers for a company
 * @param {string} companyId - The company ID
 * @returns {Promise<Array>} Array of drivers
 */
export const getAvailableDrivers = async (companyId) => {
    try {
        const { data, error } = await supabase
            .from('users')
            .select('id, username, full_name, email')
            .eq('company_id', companyId)
            .eq('role', 'driver')
            .eq('status', 'active')
            .order('full_name');

        if (error) throw error;
        return data || [];
    } catch (error) {
        console.error('Error fetching drivers:', error);
        throw error;
    }
};

/**
 * Assign a designer to a quote type
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type (roof/wall/floor)
 * @param {string} designerId - Designer user ID
 * @param {string} designerName - Designer name
 * @returns {Promise<Object>} Created designer assignment
 */
export const assignDesigner = async (jobId, quoteType, designerId, designerName, childId = null, childName = null) => {
    try {
        const { data, error } = await supabase
            .from('job_designers')
            .insert({
                job_id: jobId,
                quote_type: quoteType,
                user_id: designerId,
                name: designerName,
                is_started: false,
                is_completed: false,
                child_job_id: childId || null,
                child_job_name: childName || null
            })
            .select()
            .single();


        // Update job_quotes design status to "in_progress" — never overwrite a completed quote
        if (childId) {
            const { error: quoteError } = await supabase
                .from('job_quotes')
                .update({ design_status: 'in_progress' })
                .eq('job_id', jobId)
                .eq('id', childId)
                .eq('quote_type', quoteType)
                .neq('design_status', 'completed');

            if (quoteError) throw quoteError;
        } else {
            const { error: quoteError } = await supabase
                .from('job_quotes')
                .update({ design_status: 'in_progress' })
                .eq('job_id', jobId)
                .eq('quote_type', quoteType)
                .neq('design_status', 'completed');

            if (quoteError) throw quoteError;
        }
        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error assigning designer:', error);
        throw error;
    }
};

/**
 * Remove a designer from a quote type
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @param {string} designerId - Designer user ID
 * @returns {Promise<void>}
 */
export const removeDesigner = async (jobId, quoteType, designerId, childQuoteId = null, keepCompleted = false, childQuoteName = null) => {
    try {
        // Remove designer record — use .is() for null quoteType (old-style jobs)
        let deleteQuery = supabase
            .from('job_designers')
            .delete()
            .eq('job_id', jobId)
            .eq('user_id', designerId);

        if (quoteType !== null && quoteType !== undefined) {
            deleteQuery = deleteQuery.eq('quote_type', quoteType);
        } else {
            deleteQuery = deleteQuery.is('quote_type', null);
        }

        if (childQuoteId !== undefined && childQuoteId !== null) {
            if (childQuoteName) {
                // Named child quote: strictly target this child's row only
                deleteQuery = deleteQuery.eq('child_job_id', childQuoteId);
            } else {
                // Regular new-style OR old-style (null) row for this quote
                deleteQuery = deleteQuery.or(`child_job_id.eq.${childQuoteId},child_job_id.is.null`);
            }
        }

        const { error } = await deleteQuery;
        if (error) throw error;

        // Reset design_status to waiting_assignment unless the caller wants to keep it
        if (!keepCompleted) {
            let updateQuery = supabase
                .from('job_quotes')
                .update({ design_status: 'waiting_assignment' })
                .eq('job_id', jobId);

            if (quoteType !== null && quoteType !== undefined) {
                updateQuery = updateQuery.eq('quote_type', quoteType);
            } else {
                updateQuery = updateQuery.is('quote_type', null);
            }

            if (childQuoteId !== undefined && childQuoteId !== null) {
                updateQuery = updateQuery.eq('id', childQuoteId);
            }

            const { error: quoteError } = await updateQuery;
            if (quoteError) throw quoteError;
        }
    } catch (error) {
        console.error('Error removing designer:', error);
        throw error;
    }
};

/**
 * Remove a designer from a quote type
 * @param {string} jobId - The job ID
 * @param {string} userProfile - User
 * @param {string} type - estimator or designer
 * @param {string} quoteType - Quote type
 * @returns {Promise<void>}
 */
export const removeSelfAssignment = async (jobId, jobName, user, type, quoteType, department = null) => {
    try {

        if (type === "estimator") {

            // Check if design_status is completed
            const { data: estimationData, error: fetchError } = await supabase
                .from('job_estimators')
                .select('is_completed')
                .eq('job_id', jobId)
                .single();

            if (fetchError) throw fetchError;

            // If esitmation is completed, don't remove the estimator
            if (estimationData.is_completed) {
                throw new Error('Cannot remove estimator: estimation is already completed');
            }

            // Remove estimator
            const { error } = await supabase
                .from('job_estimators')
                .delete()
                .eq('job_id', jobId)
                .eq('user_id', user.id);

            if (error) throw error;

            // Update quote status to waiting_assignment (only if not completed)
            let updateEstimationQuery = supabase
                .from("job_quotes")
                .update({ estimation_status: "waiting_assignment" })
                .eq("job_id", jobId)
                .neq("estimation_status", "completed");

            if (department !== null) {
                updateEstimationQuery = updateEstimationQuery.eq(
                    "id",
                    department?.id
                );
            }

            const { error: quoteError } = await updateEstimationQuery;
            if (quoteError) throw quoteError;

            // 6. Create action log
            const { error: logError } = await supabase
                .from('job_action_logs')
                .insert({
                    job_id: jobId,
                    user_id: user?.id,
                    user_name: user?.full_name || user?.username,
                    user_role: user?.role,
                    action: 'removed_assignee',
                    details: `${user?.full_name || user?.username} removed itself as ${type} from the assignment ${department?.child_unique_id || jobName}`
                });

            if (logError) throw logError;


        } else if (type == "designer") {

            // Check if design_status is completed
            let selectQuery = supabase
                .from("job_quotes")
                .select("design_status")
                .eq("job_id", jobId)
                .eq("quote_type", quoteType);

            if (department !== null) {
                selectQuery = selectQuery.eq("id", department?.id);
            }

            const { data: quoteData, error: fetchError } =
                await selectQuery.maybeSingle();

            if (fetchError) throw fetchError;

            // If design is completed, don't remove the designer
            if (quoteData.design_status === 'completed') {
                throw new Error('Cannot remove designer: design is already completed');
            }

            // Remove designer
            let deleteQuery = supabase
                .from("job_designers")
                .delete()
                .eq("job_id", jobId)
                .eq("quote_type", quoteType)
                .eq("user_id", user.id);

            if (department !== null) {
                deleteQuery = deleteQuery.eq("child_job_id", department?.id);
            }

            const { error: deleteError } = await deleteQuery;
            if (deleteError) throw deleteError;


            // 3️⃣ Count remaining designers (IMPORTANT SAFETY CHECK)
            let countQuery = supabase
                .from("job_designers")
                .select("id", { count: "exact", head: true })
                .eq("job_id", jobId)
                .eq("quote_type", quoteType);

            if (department !== null) {
                countQuery = countQuery.eq("child_job_id", department?.id);
            }

            const { count, error: countError } = await countQuery;
            if (countError) throw countError;

            // 4️⃣ Only reset status if no designers remain
            if (count === 0) {
                let updateQuery = supabase
                    .from("job_quotes")
                    .update({ design_status: "waiting_assignment" })
                    .eq("job_id", jobId)
                    .eq("quote_type", quoteType)
                    .neq("design_status", "completed");

                if (department !== null) {
                    updateQuery = updateQuery.eq("id", department?.id);
                }

                const { error: quoteError } = await updateQuery;
                if (quoteError) throw quoteError;
            }


            // 6. Create action log
            const { error: logError } = await supabase
                .from('job_action_logs')
                .insert({
                    job_id: jobId,
                    user_id: user?.id,
                    user_name: user?.full_name || user?.username,
                    user_role: user?.role,
                    action: 'removed_assignee',
                    details: `${user?.full_name || user?.username} removed itself as ${type} from the assignment ${department?.child_unique_id || jobName}${quoteType && ` [${quoteType.toUpperCase()}]`}`
                });

            if (logError) throw logError;

        }
    } catch (error) {
        console.error('Error removing designer:', error);
        throw error;
    }
}


/**
 * Assign an estimator to a job
 * @param {string} jobId - The job ID
 * @param {string} estimatorId - Estimator user ID
 * @param {string} estimatorName - Estimator name
 * @returns {Promise<Object>} Created estimator assignment
 */
export const assignEstimator = async (jobId, estimatorId, estimatorName) => {
    try {
        const { data, error } = await supabase
            .from('job_estimators')
            .insert({
                job_id: jobId,
                user_id: estimatorId,
                name: estimatorName,
                is_started: false,
                is_completed: false
            })
            .select()
            .single();

        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update({ estimation_status: 'in_progress' })
            .eq('job_id', jobId)

        if (quoteError) throw quoteError;

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error assigning estimator:', error);
        throw error;
    }
};

/**
 * Remove an estimator from a job
 * @param {string} jobId - The job ID
 * @returns {Promise<void>}
 */
export const removeEstimator = async (jobId) => {
    try {
        const { error } = await supabase
            .from('job_estimators')
            .delete()
            .eq('job_id', jobId);

        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update({ estimation_status: 'waiting_assignment' })
            .eq('job_id', jobId)

        if (quoteError) throw quoteError;

        if (error) throw error;
    } catch (error) {
        console.error('Error removing estimator:', error);
        throw error;
    }
};

/**
 * Assign a driver to a delivery for a specific quote type
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type (roof/wall/floor/timber)
 * @param {string} driverId - Driver user ID
 * @param {string} driverName - Driver name
 * @param {number} deliveryCount - Delivery count (0 for first delivery, 1 for second, etc.)
 * @returns {Promise<Object>} Updated or created delivery record
 */
export const assignDriver = async (jobId, quoteType, driverId, driverName, deliveryCount = 0, childId = null, childName = null) => {
    try {

        // First, check if a delivery record exists for this job, quote type, and delivery count
        let query = supabase
            .from('job_deliveries')
            .select('*')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (childId) {
            // Search both fields: deliveries from schedule page store UUID in child_unique_id,
            // older single-assignment records stored it in child_id
            query = query.or(`child_unique_id.eq.${childId},child_id.eq.${childId}`);
        }

        // Handle both null and 0 for delivery_count (backwards compatibility)
        if (deliveryCount === 0) {
            query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
        } else {
            query = query.eq('delivery_count', deliveryCount);
        }

        const { data: existingDelivery, error: fetchError } = await query.single();

        if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
            throw fetchError;
        }

        let result;

        if (existingDelivery) {
            // Update existing delivery record
            const { data, error } = await supabase
                .from('job_deliveries')
                .update({
                    assigned_driver_id: driverId,
                    assigned_driver_name: driverName,
                    assigned_at: new Date().toISOString(),
                    delivery_status: 'scheduled'
                })
                .eq('id', existingDelivery.id)
                .select()
                .single();

            if (error) throw error;
            result = data;
        } else {
            // Create new delivery record
            const { data, error } = await supabase
                .from('job_deliveries')
                .insert({
                    job_id: jobId,
                    quote_type: quoteType,
                    is_required: true,
                    assigned_driver_id: driverId,
                    assigned_driver_name: driverName,
                    assigned_at: new Date().toISOString(),
                    delivery_status: 'scheduled',
                    delivery_count: deliveryCount,
                    child_unique_id: childId || null,  // UUID for app-level lookup
                    child_id: childId || null,          // UUID for DB unique constraint (job_deliveries_unique_child)
                })
                .select()
                .single();

            if (error) throw error;
            result = data;
        }

        return result;
    } catch (error) {
        console.error('Error assigning driver:', error);
        throw error;
    }
};

/**
 * Remove a driver from a delivery
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @param {number} deliveryCount - Delivery count (0 for first delivery, 1 for second, etc.)
 * @returns {Promise<void>}
 */
export const removeDriver = async (jobId, quoteType, deliveryCount = 0, childId = null) => {
    try {
        let query = supabase
            .from('job_deliveries')
            .update({
                assigned_driver_id: null,
                assigned_driver_name: null,
                assigned_at: null,
                assigned_by_user_id: null,
                assigned_by_name: null,
                delivery_status: 'not_scheduled'
            })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (childId !== null) {
            query = query.eq("child_unique_id", childId);
        }

        // Handle both null and 0 for delivery_count (backwards compatibility)
        if (deliveryCount === 0) {
            query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
        } else {
            query = query.eq('delivery_count', deliveryCount);
        }

        const { data, error } = await query.select();

        if (error) throw error;
        return data?.[0] || null;
    } catch (error) {
        console.error('Error removing driver:', error);
        throw error;
    }
};

/**
 * Confirm package receipt (driver action)
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @param {number} deliveryCount - Delivery count (0 for first delivery, 1 for second, etc.)
 * @returns {Promise<Object>} Updated delivery record
 */
export const confirmPackageReceipt = async (jobId, quoteType, deliveryCount = 0, childId = null) => {
    try {
        let query = supabase
            .from('job_deliveries')
            .update({ delivery_status: 'in_transit' })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (childId) {
            query = query.eq('child_unique_id', childId);
        } else {
            query = query.is('child_unique_id', null);
            if (deliveryCount === 0) {
                query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
            } else {
                query = query.eq('delivery_count', deliveryCount);
            }
        }

        const { data, error } = await query.select();
        if (error) throw error;

        // Update job_quotes with same logic
        let quotesQuery = supabase
            .from('job_quotes')
            .update({ delivery_status: 'in_transit' })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (childId) {
            quotesQuery = quotesQuery.eq('id', childId);
        } else if (deliveryCount === 0) {
            quotesQuery = quotesQuery.or(`delivery_count.is.null,delivery_count.eq.0`);
        } else {
            quotesQuery = quotesQuery.eq('delivery_count', deliveryCount);
        }

        const { error: quotesError } = await quotesQuery;
        if (quotesError) throw quotesError;

        return data?.[0] || null;
    } catch (error) {
        console.error('Error confirming package receipt:', error);
        throw error;
    }
};

/**
 * Upload delivery photo to public bucket
 * @param {string} jobId - The job ID
 * @param {File} file - Photo file to upload
 * @param {string} quoteType - Quote type (roof/wall/floor/timber)
 * @param {string} userId - User ID uploading
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @param {string} companySlug - Company slug
 * @param {number} deliveryCount - Delivery count (0 for first delivery, 1 for second, etc.)
 * @returns {Promise<Object>} Uploaded file record
 */
export const uploadDeliveryPhoto = async (jobId, file, quoteType, userId, userName, userRole, companySlug, deliveryCount = 0, childId = null, childName = null) => {
    try {
        const normalizedFile = await normalizeUploadFile(file);

        // Upload to public storage bucket
        const safeName = sanitizeFileName(normalizedFile.name);
        const filename = `${companySlug}/${jobId}/${Date.now()}-${safeName}`;


        const { data: storageData, error: storageError } = await supabase.storage
            .from('delivery-uploads') // Public bucket for delivery photos
            .upload(filename, normalizedFile, {
                cacheControl: '3600',
                upsert: false
            });

        if (storageError) throw storageError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('delivery-uploads')
            .getPublicUrl(filename);

        // Create file record
        const { data, error } = await supabase
            .from('job_files')
            .insert({
                job_id: jobId,
                filename: filename,
                original_name: file.name,
                url: publicUrl,
                uploaded_by_user_id: userId,
                uploaded_by_name: userName,
                uploaded_by_role: userRole,
                file_type: 'delivery',
                quote_type: quoteType,
                delivery_count: deliveryCount,
                child_id: childId || null,
                child_unique_id: childName || null
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error uploading delivery photo:', error);
        throw error;
    }
};

export const deleteDeliveryPhoto = async (fileId, filename) => {
    const { error: storageError } = await supabase.storage
        .from('delivery-uploads')
        .remove([filename]);
    if (storageError) console.error('Error removing from storage:', storageError);

    const { error } = await supabase
        .from('job_files')
        .delete()
        .eq('id', fileId);
    if (error) throw error;
};

/**
 * Complete delivery (driver action)
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type
 * @param {string} driverId - Driver user ID
 * @param {string} driverName - Driver name
 * @param {string} deliveryNotes - Optional delivery notes
 * @param {number} deliveryCount - Delivery count (0 for first delivery, 1 for second, etc.)
 * @returns {Promise<Object>} Updated delivery record
 */
export const completeDelivery = async (jobId, quoteType, driverId, driverName, deliveryNotes = '', deliveryCount = 0, childId = null) => {
    try {
        let query = supabase
            .from('job_deliveries')
            .update({
                delivery_status: 'delivered',
                delivered_at: new Date().toISOString(),
                delivered_by_user_id: driverId,
                delivered_by_name: driverName,
                delivery_notes: deliveryNotes
            })
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (childId) {
            query = query.eq('child_unique_id', childId);
        } else {
            // Restrict to rows without a child link so named-child deliveries aren't affected
            query = query.is('child_unique_id', null);
            if (deliveryCount === 0) {
                query = query.or(`delivery_count.is.null,delivery_count.eq.0`);
            } else {
                query = query.eq('delivery_count', deliveryCount);
            }
        }

        const { data, error } = await query.select();
        if (error) throw error;
        return data?.[0] || null;
    } catch (error) {
        console.error('Error completing delivery:', error);
        throw error;
    }
};


/**
 * Complete a design for a specific quote type with file uploads
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type (roof/wall/floor)
 * @param {string} designerId - Designer user ID
 * @param {string} designerName - Designer name
 * @param {string} completionNote - Completion note
 * @param {Array<File>} files - Array of files to upload
 * @param {string} companySlug - Company slug
 * @returns {Promise<Object>} Updated designer record with uploaded files
 */
export const completeDesign = async (jobId, quoteType, designerId, designerName, completionNote, files, companySlug, userData, department = null) => {
    try {
        // Upload all files first
        const uploadedFiles = [];

        const isAdmin = ['admin', 'super_admin'].includes(userData?.role);
        const completedById = userData.id;
        const completedByName = userData.username;

        // 1️⃣ Find assigned designer
        let getExistingDesigner = supabase
            .from('job_designers')
            .select('*')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (department?.child_unique_id) {
            // Named child quote (new system: "W1", "W2") — strict UUID match
            getExistingDesigner = getExistingDesigner.eq('child_job_id', department.id);
        } else if (department) {
            // Regular quote (no child_unique_id):
            //   new-style assignment → child_job_id = quote.id
            //   old-style assignment → child_job_id is null
            // Prefer new-style if both exist; limit(1) makes maybeSingle() safe.
            getExistingDesigner = getExistingDesigner
                .or(`child_job_id.eq.${department.id},child_job_id.is.null`)
                .order('child_job_id', { ascending: false, nullsLast: true })
                .limit(1);
        }

        const { data: existingDesigner, error: fetchDesignerError } =
            await getExistingDesigner.maybeSingle();
        if (fetchDesignerError) throw fetchDesignerError;

        const realDesignerId = existingDesigner?.user_id || designerId || completedById;
        const realDesignerName =
            existingDesigner?.designer_name ||
            designerName ||
            userData.full_name ||
            userData.username;


        // 2️⃣ Upload files with correct designer identity
        for (const file of files) {
            try {
                const fileRecord = await uploadJobFile(
                    jobId,
                    file,
                    'design',
                    quoteType,
                    realDesignerId,
                    realDesignerName,
                    'designer',
                    companySlug,
                    department
                );
                uploadedFiles.push(fileRecord);
            } catch (err) {
                console.error(`File upload failed: ${file.name}`, err);
            }
        }

        let designerRecord;


        // 3️⃣ If designer exists → update
        if (existingDesigner) {
            const { data, error } = await supabase
                .from('job_designers')
                .update({
                    is_completed: true,
                    completed_at: new Date().toISOString(),
                    finishing_note: completionNote,
                    completed_by: completedById, // ✅ IMPORTANT
                    completed_by_name: completedByName
                })
                .eq('id', existingDesigner.id)
                .select()
                .single();

            if (error) throw error;
            designerRecord = data;
        }

        // 4️⃣ If designer does NOT exist → only admin can create
        else {
            if (!isAdmin) {
                throw new Error("You are not assigned to this design.");
            }

            const { data, error } = await supabase
                .from('job_designers')
                .insert({
                    job_id: jobId,
                    quote_type: quoteType,
                    user_id: realDesignerId,
                    name: realDesignerName,
                    is_completed: true,
                    completed_at: new Date().toISOString(),
                    finishing_note: completionNote,
                    completed_by: completedById,
                    completed_by_name: completedByName,
                    child_job_id: department?.id || null,
                    child_job_name: department?.child_unique_id || null
                })
                .select()
                .single();

            if (error) throw error;
            designerRecord = data;
        }

        // 5️⃣ Update quote status
        let selectQuery = supabase
            .from('job_quotes')
            .select('estimation_status')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .is('delivery_count', null);

        if (department !== undefined && department !== null) {
            selectQuery = selectQuery.eq('id', department?.id);
        }

        const { data: currentQuote } = await selectQuery.single();

        const quoteUpdate = { design_status: 'completed' };

        if (currentQuote?.estimation_status === 'waiting_design') {
            quoteUpdate.estimation_status = 'waiting_assignment';
        }

        let updateQuery = supabase
            .from('job_quotes')
            .update(quoteUpdate)
            .eq('job_id', jobId)
            .eq('quote_type', quoteType)
            .is('delivery_count', null);

        if (department !== undefined && department !== null) {
            updateQuery = updateQuery.eq('id', department?.id);
        }

        const { error: quoteError } = await updateQuery;

        if (quoteError) console.error('Error updating quote:', quoteError);

        return {
            ...designerRecord,
            uploaded_files: uploadedFiles
        };
    } catch (error) {
        console.error('Error completing design:', error);
        throw error;
    }
};

/**
 * Complete estimation for a job with file uploads
 * @param {string} jobId - The job ID
 * @param {string} estimatorId - Estimator user ID
 * @param {string} estimatorName - Estimator name
 * @param {string} completionNote - Completion note
 * @param {Array<File>} files - Array of files to upload
 * @param {string} companySlug - Company slug
 * @returns {Promise<Object>} Updated estimator record with uploaded files
 */
export const completeEstimation = async (
    jobId,
    estimatorId,
    estimatorName,
    completionNote,
    files,
    companySlug,
    user
) => {
    try {

        const uploadedFiles = [];

        const isAdmin = ['admin', 'super_admin'].includes(user?.role);
        const completedById = user.id;
        const completedByName = user.username;

        // 2) Check if estimator assignment exists
        const { data: existingEstimator, error: fetchEstimatorError } = await supabase
            .from('job_estimators')
            .select('*')
            .eq('job_id', jobId)
            .maybeSingle();

        if (fetchEstimatorError) throw fetchEstimatorError;

        const realEstimatorId = existingEstimator?.user_id || estimatorId || completedById;
        const realEstimatorName =
            existingEstimator?.name ||
            estimatorName ||
            user.full_name ||
            user.username;

        // // 1) Upload files
        for (const file of files) {
            try {
                const fileRecord = await uploadJobFile(
                    jobId,
                    file,
                    'estimation',
                    null,
                    realEstimatorId,
                    realEstimatorName,
                    user?.role || "estimator",
                    companySlug
                );
                uploadedFiles.push(fileRecord);
            } catch (err) {
                console.error(`File upload failed: ${file.name}`, err);
            }
        }

        let estimatorRecord;

        // 3️⃣ If designer exists → update
        if (existingEstimator) {
            const { data, error } = await supabase
                .from('job_estimators')
                .update({
                    is_completed: true,
                    completed_at: new Date().toISOString(),
                    finishing_note: completionNote,
                    completed_by: completedById, // ✅ IMPORTANT
                    completed_by_name: completedByName
                })
                .eq('id', existingEstimator.id)
                .select()
                .single();

            if (error) throw error;
            estimatorRecord = data;
        }



        // 4️⃣ If designer does NOT exist → only admin can create
        else {
            if (!isAdmin) {
                throw new Error("You are not assigned to this design.");
            }

            const { data, error } = await supabase
                .from('job_estimators')
                .insert({
                    job_id: jobId,
                    user_id: realEstimatorId,
                    name: realEstimatorName,
                    is_completed: true,
                    completed_at: new Date().toISOString(),
                    finishing_note: completionNote,
                    completed_by: completedById,
                    completed_by_name: completedByName
                })
                .select()
                .single();

            if (error) throw error;
            estimatorRecord = data;
        }

        const quoteUpdate = { estimation_status: 'completed' };

        sendEstimationCompletedNotifications({
            jobId,
            companyId: user.company_id,
            companySlug,
            completedById,
            completedByName,
            estimatorId: realEstimatorId,
            estimatorName: realEstimatorName,
            completionNote,
            uploadedFileIds: uploadedFiles.map(f => f.id),
        });

        const { error: quoteError } = await supabase
            .from('job_quotes')
            .update(quoteUpdate)
            .eq('job_id', jobId)
            .is('delivery_count', null);

        if (quoteError) console.error('Error updating quotes:', quoteError);

        return {
            ...estimatorRecord,
            uploaded_files: uploadedFiles
        };


    } catch (error) {
        console.error('Error completing estimation:', error);
        throw error;
    }
};


/**
 * Upload job files with proper path structure
 * @param {string} jobId - The job ID
 * @param {File} file - File to upload
 * @param {string} fileType - Type of file (initial/design/estimation/delivery)
 * @param {string} quoteType - Quote type (optional, null for estimation)
 * @param {string} userId - User ID uploading
 * @param {string} userName - User name
 * @param {string} userRole - User role
 * @param {string} companySlug - Company slug for path
 * @returns {Promise<Object>} Uploaded file record
 */
export const uploadJobFile = async (jobId, file, fileType, quoteType, userId, userName, userRole, companySlug, department) => {
    try {
        // Create proper path structure: companySlug/jobId/type-timestamp-filename
        const timestamp = Date.now();
        const sanitizedFileName = sanitizeFileName(file.name);
        const filename = `${companySlug}/${jobId}/${fileType}-${timestamp}-${sanitizedFileName}`;

        // Upload to storage
        const { data: storageData, error: storageError } = await supabase.storage
            .from('job-files')
            .upload(filename, file, {
                cacheControl: '3600',
                upsert: false
            });

        if (storageError) throw storageError;

        // Create file record in database
        const { data, error } = await supabase
            .from('job_files')
            .insert({
                job_id: jobId,
                filename: filename,
                original_name: file.name,
                url: filename, // Store the path, not public URL since it's a private bucket
                uploaded_by_user_id: userId,
                uploaded_by_name: userName,
                uploaded_by_role: userRole,
                file_type: fileType,
                quote_type: quoteType,
                child_id: department?.id || null,
                child_unique_id: department?.child_unique_id || null
            })
            .select()
            .single();


        if (error) throw error;
        return data;
    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};

/**
 * Subscribe to single job updates
 * @param {string} jobId - The job ID
 * @param {Function} callback - Callback function for updates
 * @returns {Object} Supabase channel subscription
 */
export const subscribeToJobUpdates = (jobId, callback) => {
    const channel = supabase
        .channel(`job-${jobId}-changes`)
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'jobs',
                filter: `id=eq.${jobId}`
            },
            callback
        )
        .subscribe();

    return channel;
};

/**
 * Toggle Quote Sent status
 */
export const toggleQuoteSent = async (jobId, currentStatus) => {
    try {
        const { data, error } = await supabase
            .from('jobs')
            .update({ quote_sent: !currentStatus })
            .eq('id', jobId)
            .select()
            .single();

        if (error) throw error;

        return data;
    } catch (error) {
        console.error('Error toggling quote sent status:', error);
        throw error;
    }
};

/**
 * Create a new delivery record in job_quotes
 * @param {string} jobId - The job ID
 * @param {string} quoteType - Quote type (roof, wall, floor)
 * @param {string} deliveryNote - Delivery note
 * @param {string} deliveryTimeSlot - Delivery time slot (1st, 2nd, 3rd, 4th, 5th)
 * @param {string} expectedDeliveryDate - Expected delivery date (YYYY-MM-DD format)
 * @param {Array<number>} selectedUnits - Selected unit numbers (for unit properties)
 * @returns {Promise<object>} Created delivery record
 */
export const createNewDelivery = async (jobId, quoteType, deliveryNote, deliveryTimeSlot, expectedDeliveryDate, selectedUnits = []) => {
    try {
        // First, get the count of existing records for this job_id and quote_type
        // This will be used to calculate delivery_count
        const { data: existingQuotes, error: countError } = await supabase
            .from('job_quotes')
            .select('id')
            .eq('job_id', jobId)
            .eq('quote_type', quoteType);

        if (countError) throw countError;

        const deliveryCount = existingQuotes?.length ?? 0;

        // Convert date to ISO format if provided
        const formattedDate = expectedDeliveryDate ? new Date(expectedDeliveryDate).toISOString() : null;

        // 2️⃣ Create job_quote
        const { data: quote, error: quoteError } = await supabase
            .from('job_quotes')
            .insert({
                job_id: jobId,
                quote_type: quoteType,
                is_required: true,
                design_status: 'completed',
                estimation_status: 'completed',
                delivery_status: 'not_scheduled',
                delivery_note: deliveryNote || null,
                delivery_time_slot: deliveryTimeSlot || null,
                expected_delivery_date: formattedDate,
                selected_units: selectedUnits.length > 0 ? selectedUnits : null,
                delivery_count: deliveryCount
            })
            .select()
            .single();

        if (quoteError) throw quoteError;

        // 3️⃣ Create matching job_delivery
        const { data: delivery, error: deliveryError } = await supabase
            .from('job_deliveries')
            .insert({
                job_id: jobId,
                quote_type: quoteType,
                delivery_status: 'not_scheduled',
                delivery_count: deliveryCount,
                delivery_date: formattedDate,
                is_required: true
            })
            .select()
            .single();

        if (deliveryError) throw deliveryError;


        return {
            quote,
            delivery
        };
    } catch (error) {
        console.error('Error creating new delivery:', error);
        throw error;
    }
};

/**
 * Delete a job permanently
 * @param {string} jobId - The job ID
 * @param {string} companyId - The company ID for validation
 * @returns {Promise<void>}
 */
export const deleteJob = async (jobId, companyId, user) => {
    try {
        // First verify the job belongs to the company
        const { data: job, error: fetchError } = await supabase
            .from('jobs')
            .select('id, company_id, unique_id')
            .eq('id', jobId)
            .eq('company_id', companyId)
            .single();

        if (fetchError) throw fetchError;
        if (!job) throw new Error('Job not found or unauthorized');


        // Soft delete by setting deleted flag to true
        const { error: deleteError } = await supabase
            .from('jobs')
            .update({ deleted: true })
            .eq('id', jobId)
            .eq('company_id', companyId);

        if (deleteError) throw deleteError;


        // 6. Create action log
        const { error: logError } = await supabase
            .from('job_action_logs')
            .insert({
                job_id: jobId,
                user_id: user?.id,
                user_name: user?.full_name || user?.username,
                user_role: user?.role,
                action: 'job_deleted',
                details: `Job ${job.unique_id} soft-deleted.`
            });

        if (logError) throw logError;

        return { success: true };
    } catch (error) {
        console.error('Error deleting job:', error);
        throw error;
    }
};